import { Activity } from 'lucide-react';
import { Stat, DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Card, CardBody, Badge } from '@vnetwork/ui/primitives';
import { Section } from '@vnetwork/ui/layout';
import {
  useInstanceSflow,
  useInstanceSflowSources,
  type Instance,
  type SFlowLog,
  type SFlowSource,
} from '@/api/opennebula';
import { formatBytes } from '@/pages/services/_shared/analytics-format';

const PROTO: Record<number, string> = { 1: 'ICMP', 6: 'TCP', 17: 'UDP' };
const proto = (n?: number) => (n == null ? '—' : PROTO[n] ?? String(n));

/**
 * sFlow-based network traffic analysis (BETA). Shows aggregate bandwidth, the
 * top source IPs talking to the instance, and recent flow records. Endpoints
 * return 404 when sFlow isn't enabled for the zone/instance — handled as empty.
 */
export function NetworkTrafficTab({ serviceId, instance }: { serviceId: string; instance: Instance }) {
  const { data: sources, isLoading: sLoading, isError: sErr } = useInstanceSflowSources(serviceId, instance.id, { limit: 20 });
  const { data: flows, isLoading: fLoading, isError: fErr } = useInstanceSflow(serviceId, instance.id, { limit: 50, direction: 'outgoing' });

  const summary = sources?.summary ?? flows?.summary ?? {};
  const hasData = (sources?.sources.length ?? 0) > 0 || (flows?.logs.length ?? 0) > 0;
  const loading = sLoading || fLoading;
  const unavailable = (sErr && fErr) || (!loading && !hasData);

  const sourceCols: ColumnDef<SFlowSource, unknown>[] = [
    { id: 'ip', header: 'Source IP', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.src_ip || '—'}</span> },
    { id: 'conn', header: 'Connections', cell: ({ row }) => row.original.connection_count ?? 0 },
    { id: 'bytes', header: 'Bytes', cell: ({ row }) => formatBytes(row.original.total_bytes) },
    { id: 'pkts', header: 'Packets', cell: ({ row }) => (row.original.total_packets ?? 0).toLocaleString() },
    { id: 'proto', header: 'Protocols', cell: ({ row }) => (row.original.protocols ?? []).map(proto).join(', ') || '—' },
  ];
  const flowCols: ColumnDef<SFlowLog, unknown>[] = [
    { id: 'src', header: 'Source', cell: ({ row }) => `${row.original.src_ip ?? '—'}:${row.original.src_port ?? '—'}` },
    { id: 'dst', header: 'Destination', cell: ({ row }) => `${row.original.dst_ip ?? '—'}:${row.original.dst_port ?? '—'}` },
    { id: 'proto', header: 'Protocol', cell: ({ row }) => proto(row.original.protocol) },
    { id: 'bytes', header: 'Bytes', cell: ({ row }) => formatBytes(row.original.bytes) },
    { id: 'pps', header: 'pps', cell: ({ row }) => (row.original.pps ?? 0).toLocaleString() },
  ];

  return (
    <Section
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          Network traffic
          <Badge tone="info" appearance="subtle" size="sm">Beta</Badge>
        </span>
      }
      description="sFlow-based bandwidth, top source IPs and recent flows for this instance."
    >
      {unavailable ? (
        <EmptyState
          icon={<Activity size={28} />}
          title="No traffic data"
          description="sFlow traffic analysis isn't enabled for this instance's zone, or there's no recent traffic to report."
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--vn-space-3)' }}>
            <Card flat><CardBody><Stat label="Bandwidth" value={`${(summary.total_bandwidth_mbps ?? 0).toFixed(2)} Mbps`} /></CardBody></Card>
            <Card flat><CardBody><Stat label="Total traffic" value={formatBytes(summary.total_bytes)} /></CardBody></Card>
            <Card flat><CardBody><Stat label="Packets" value={(summary.total_packets ?? 0).toLocaleString()} /></CardBody></Card>
            <Card flat><CardBody><Stat label="Unique sources" value={(summary.unique_sources ?? sources?.total ?? 0).toLocaleString()} /></CardBody></Card>
          </div>

          <Section title="Top source IPs">
            <DataTable
              columns={sourceCols}
              data={sources?.sources ?? []}
              getRowId={(r, i) => r.src_ip ?? String(i)}
              emptyState={<p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>No source data.</p>}
            />
          </Section>

          <Section title="Recent flows">
            <DataTable
              columns={flowCols}
              data={flows?.logs ?? []}
              getRowId={(_r, i) => String(i)}
              emptyState={<p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>No recent flows.</p>}
            />
          </Section>
        </div>
      )}
    </Section>
  );
}
